import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import {
  createClient as defaultCreateClient,
  createClientContact as defaultCreateContact,
  queryClients as defaultQueryClients,
  queryClientContacts as defaultQueryContacts,
} from "../lib/clientDirectory";
import { readCache, writeCache } from "../lib/cache";
import type { ClientContact, ClientSuggestion, CreateClientDraft, CreateContactDraft } from "../types";
import { ClientForm } from "./ClientForm";
import { ContactForm } from "./ContactForm";
import styles from "./ClientContactField.module.css";

const CLIENT_DEBOUNCE_MS = 250;

export interface ClientContactFieldValue {
  id: string;
  name: string;
}

export interface ClientContactFieldProps {
  /** Currently selected client id (seeds the field on edit). */
  clientId?: string;
  /** Currently selected client display name. */
  clientName?: string;
  /** Currently selected contact id. */
  clientContactId?: string;
  /** Currently selected contact display name. */
  clientContactName?: string;
  /**
   * Called when a client is chosen or created, and with `null` when the typed
   * text no longer matches the chosen client. Clearing the client must clear the
   * contact too.
   */
  onSelectClient: (client: ClientContactFieldValue | null) => void;
  /** Called when a contact is chosen or created, and with `null` when cleared. */
  onSelectContact: (contact: ClientContactFieldValue | null) => void;
  /**
   * Renders the contact half. Off for hosts that only need the client — the
   * invoice builder bills an organisation, and a contact it captured would go
   * nowhere. Defaults on: the job form has always shown both.
   */
  showContact?: boolean;
  search?: (query: string) => Promise<ClientSuggestion[]>;
  createClient?: (draft: CreateClientDraft) => Promise<ClientSuggestion>;
  /** Lists contacts for the given client id (used once a client is chosen). */
  loadContacts?: (clientId: string) => Promise<ClientContact[]>;
  /** Creates a contact under the given client. */
  createContact?: (clientId: string, draft: CreateContactDraft) => Promise<ClientContact>;
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

function matchesContact(contact: ClientContact, needle: string): boolean {
  return [contact.displayName, contact.email, contact.phone].some((field) =>
    (field ?? "").toLowerCase().includes(needle),
  );
}

/**
 * Inline client + contact picker for the New Job form.
 *
 * Renders two comboboxes: the first searches/creates a client, the second (enabled
 * only once a client is chosen) searches/creates a contact for that client. Both
 * support an inline create panel and full keyboard operation.
 *
 * The committed selection and the text in the box are kept honest with each
 * other: editing the text away from the chosen name clears the id held by the
 * parent, so a submitted draft can never carry a client the field is no longer
 * showing. `syncedClientName` records the last name this component and its
 * parent agreed on, which is what tells an external reset apart from the echo of
 * our own update.
 */
export function ClientContactField({
  clientId,
  clientName,
  clientContactId,
  clientContactName,
  onSelectClient,
  onSelectContact,
  showContact = true,
  search = defaultQueryClients,
  createClient = defaultCreateClient,
  loadContacts = defaultQueryContacts,
  createContact = defaultCreateContact,
}: ClientContactFieldProps) {
  const [clientQuery, setClientQuery] = useState(clientName ?? "");
  const [clientMatches, setClientMatches] = useState<ClientSuggestion[]>([]);
  const [clientOpen, setClientOpen] = useState(false);
  const [clientActive, setClientActive] = useState(-1);
  const [clientError, setClientError] = useState("");
  const [creatingClient, setCreatingClient] = useState(false);
  const clientRootRef = useRef<HTMLDivElement>(null);
  const syncedClientName = useRef(clientName ?? "");

  const [selectedClientId, setSelectedClientId] = useState<string | undefined>(clientId);
  const [contactQuery, setContactQuery] = useState(clientContactName ?? "");
  const [contactMatches, setContactMatches] = useState<ClientContact[]>([]);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactActive, setContactActive] = useState(-1);
  const [contactError, setContactError] = useState("");
  const [creatingContact, setCreatingContact] = useState(false);
  const contactRootRef = useRef<HTMLDivElement>(null);

  /** Pushes a client to the parent and records what we last agreed on. */
  function commitClient(next: ClientContactFieldValue | null): void {
    syncedClientName.current = next ? next.name : "";
    onSelectClient(next);
  }

  // Debounced client search, served from a short-lived cache. `active` guards the
  // response, not just the timer: the debounce cannot cancel a request already in
  // flight, and without this a slow early response overwrites a later one.
  useEffect(() => {
    const q = clientQuery.trim();
    const cacheKey = `clients:${q.toLowerCase()}`;
    const cached = readCache<ClientSuggestion[]>(cacheKey);
    if (cached) {
      setClientMatches(cached);
      setClientError("");
      return;
    }
    let active = true;
    const handle = setTimeout(() => {
      search(q)
        .then((results) => {
          if (!active) return;
          writeCache(cacheKey, results);
          setClientMatches(results);
          setClientError("");
        })
        .catch((error: unknown) => {
          if (!active) return;
          setClientMatches([]);
          setClientError(errorMessage(error, "Could not load clients"));
        });
    }, CLIENT_DEBOUNCE_MS);
    return () => {
      active = false;
      clearTimeout(handle);
    };
  }, [clientQuery, search]);

  // Keep the client input in step with an external reset (e.g. after submit), but
  // ignore the parent echoing back what we just sent it — that would wipe the text
  // the user is typing at the moment we clear a stale selection.
  useEffect(() => {
    const incoming = clientName ?? "";
    if (incoming === syncedClientName.current) return;
    syncedClientName.current = incoming;
    setClientQuery(incoming);
  }, [clientName]);

  // When the selected client changes, reset the contact picker and reload contacts.
  useEffect(() => {
    setContactError("");
    if (!selectedClientId) {
      setContactMatches([]);
      setContactQuery("");
      return;
    }
    let active = true;
    loadContacts(selectedClientId)
      .then((contacts) => {
        if (!active) return;
        setContactMatches(contacts);
        if (!clientContactId) return;
        const match = contacts.find((contact) => contact.id === clientContactId);
        if (match) {
          setContactQuery(match.displayName);
          return;
        }
        // The saved contact has been removed. Say so and drop the id rather than
        // showing an empty box and re-persisting a contact that no longer exists.
        setContactQuery("");
        setContactError("The saved contact is no longer available. Pick another.");
        onSelectContact(null);
      })
      .catch((error: unknown) => {
        if (!active) return;
        setContactMatches([]);
        setContactError(errorMessage(error, "Could not load contacts"));
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId]);

  // A new result set invalidates the highlighted row, which is otherwise left
  // pointing past the end of a list that has since shrunk.
  useEffect(() => {
    setClientActive(-1);
  }, [clientMatches]);

  useEffect(() => {
    setContactActive(-1);
  }, [contactMatches, contactQuery]);

  // Close dropdowns on outside interaction.
  useEffect(() => {
    function onPointer(event: MouseEvent) {
      if (clientRootRef.current && !clientRootRef.current.contains(event.target as Node)) {
        setClientOpen(false);
      }
      if (contactRootRef.current && !contactRootRef.current.contains(event.target as Node)) {
        setContactOpen(false);
      }
    }
    document.addEventListener("mousedown", onPointer);
    return () => document.removeEventListener("mousedown", onPointer);
  }, []);

  function selectClient(suggestion: ClientSuggestion): void {
    commitClient({ id: suggestion.id, name: suggestion.name });
    if (clientContactId) onSelectContact(null);
    setSelectedClientId(suggestion.id);
    setClientQuery(suggestion.name);
    setClientError("");
    setContactQuery("");
    setContactMatches([]);
  }

  function chooseClient(suggestion: ClientSuggestion): void {
    selectClient(suggestion);
    setClientOpen(false);
    setClientActive(-1);
  }

  function handleClientCreated(client: ClientSuggestion): void {
    selectClient(client);
    setClientMatches((prev) =>
      prev.some((candidate) => candidate.id === client.id) ? prev : [...prev, client],
    );
    setCreatingClient(false);
  }

  function handleClientInput(text: string): void {
    setClientQuery(text);
    setClientOpen(true);
    setClientActive(-1);
    // Free text is not a selection. Drop the committed client (and its contact)
    // the moment the box stops showing the client's name.
    if (clientId && text !== clientName) {
      commitClient(null);
      setSelectedClientId(undefined);
    }
  }

  function chooseContact(contact: ClientContact): void {
    onSelectContact({ id: contact.id, name: contact.displayName });
    setContactQuery(contact.displayName);
    setContactError("");
    setContactOpen(false);
    setContactActive(-1);
  }

  function handleContactCreated(contact: ClientContact): void {
    onSelectContact({ id: contact.id, name: contact.displayName });
    setContactQuery(contact.displayName);
    setContactMatches((prev) => (prev.some((c) => c.id === contact.id) ? prev : [...prev, contact]));
    setContactError("");
    setCreatingContact(false);
  }

  function handleContactInput(text: string): void {
    setContactQuery(text);
    setContactOpen(true);
    setContactActive(-1);
    if (clientContactId && text !== clientContactName) onSelectContact(null);
  }

  const clientRows: Array<{ kind: "client"; suggestion: ClientSuggestion } | { kind: "create-client" }> = [
    ...clientMatches.map((suggestion) => ({ kind: "client" as const, suggestion })),
    { kind: "create-client" as const },
  ];

  // Text that is simply the chosen contact's name is not a search — filtering on
  // it would narrow the list to the one contact already selected, leaving no way
  // to pick a different one without first clearing the box.
  const contactNeedle = contactQuery.trim().toLowerCase();
  const browsing = !contactNeedle || contactQuery === clientContactName;
  const visibleContacts = browsing
    ? contactMatches
    : contactMatches.filter((contact) => matchesContact(contact, contactNeedle));

  const contactRows: Array<{ kind: "contact"; contact: ClientContact } | { kind: "create-contact" }> = [
    ...visibleContacts.map((contact) => ({ kind: "contact" as const, contact })),
    { kind: "create-contact" as const },
  ];

  function onClientKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setClientOpen(true);
      setClientActive((index) => Math.min(index + 1, clientRows.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setClientActive((index) => Math.max(index - 1, -1));
    } else if (event.key === "Enter") {
      // The row can be gone: results may have arrived after the highlight moved.
      const row = clientOpen && clientActive >= 0 ? clientRows[clientActive] : undefined;
      if (!row) return;
      event.preventDefault();
      if (row.kind === "client") chooseClient(row.suggestion);
      else setCreatingClient(true);
    } else if (event.key === "Escape") {
      setClientOpen(false);
    }
  }

  function onContactKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setContactOpen(true);
      setContactActive((index) => Math.min(index + 1, contactRows.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setContactActive((index) => Math.max(index - 1, -1));
    } else if (event.key === "Enter") {
      const row = contactOpen && contactActive >= 0 ? contactRows[contactActive] : undefined;
      if (!row) return;
      event.preventDefault();
      if (row.kind === "contact") chooseContact(row.contact);
      else if (selectedClientId) setCreatingContact(true);
    } else if (event.key === "Escape") {
      setContactOpen(false);
    }
  }

  return (
    <div className={styles.root}>
      <div className={styles.field} ref={clientRootRef}>
        <div className={styles.labelRow}>
          <span className={styles.label}>Client</span>
          <button
            type="button"
            className={styles.link}
            onMouseDown={(event) => {
              event.preventDefault();
              setCreatingClient(true);
              setClientOpen(false);
            }}
          >
            + client
          </button>
        </div>
        <input
          id="client-autocomplete-input"
          className={styles.input}
          name="client"
          value={clientQuery}
          placeholder="Search clients…"
          aria-label="Client"
          aria-autocomplete="list"
          aria-expanded={clientOpen}
          aria-controls="client-autocomplete-list"
          aria-describedby={clientError ? "client-autocomplete-error" : undefined}
          role="combobox"
          autoComplete="off"
          onChange={(event) => handleClientInput(event.target.value)}
          onFocus={() => setClientOpen(true)}
          onKeyDown={onClientKeyDown}
        />
        {clientError ? (
          <span id="client-autocomplete-error" role="alert" className={styles.fieldError}>
            {clientError}
          </span>
        ) : null}
        {creatingClient ? (
          <ClientForm
            saveClient={createClient}
            onSaved={handleClientCreated}
            onCancel={() => setCreatingClient(false)}
          />
        ) : null}
        {clientOpen && !creatingClient ? (
          <ul className={styles.list} id="client-autocomplete-list" role="listbox">
            {clientRows.map((row, index) => {
              const isCommand = row.kind !== "client";
              const primary = row.kind === "client" ? row.suggestion.name : "Create Client";
              const meta =
                row.kind === "client" ? row.suggestion.detail : "Add a new client to the directory";
              return (
                <li
                  key={row.kind === "client" ? row.suggestion.id : row.kind}
                  role="option"
                  aria-selected={index === clientActive}
                  className={`${styles.item}${isCommand ? ` ${styles.command}` : ""}${index === clientActive ? ` ${styles.itemActive}` : ""}`}
                  onMouseEnter={() => setClientActive(index)}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    if (row.kind === "client") chooseClient(row.suggestion);
                    else setCreatingClient(true);
                  }}
                >
                  <span className={styles.primary}>{primary}</span>
                  {meta ? <span className={styles.meta}>{meta}</span> : null}
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>

      {/* Not `hidden`: .field sets display:flex, which outranks the UA rule. */}
      {showContact && (
      <div className={styles.field} ref={contactRootRef}>
        <div className={styles.labelRow}>
          <span className={styles.label}>Contact</span>
          <button
            type="button"
            className={styles.link}
            disabled={!selectedClientId}
            onMouseDown={(event) => {
              event.preventDefault();
              if (selectedClientId) {
                setCreatingContact(true);
                setContactOpen(false);
              }
            }}
          >
            + contact
          </button>
        </div>
        <input
          id="contact-autocomplete-input"
          className={styles.input}
          name="contact"
          value={contactQuery}
          placeholder={selectedClientId ? "Search contacts…" : "Select a client first"}
          aria-label="Client contact"
          aria-autocomplete="list"
          aria-expanded={contactOpen}
          aria-controls="contact-autocomplete-list"
          aria-describedby={contactError ? "contact-autocomplete-error" : undefined}
          role="combobox"
          autoComplete="off"
          disabled={!selectedClientId}
          onChange={(event) => handleContactInput(event.target.value)}
          onFocus={() => selectedClientId && setContactOpen(true)}
          onKeyDown={onContactKeyDown}
        />
        {contactError ? (
          <span id="contact-autocomplete-error" role="alert" className={styles.fieldError}>
            {contactError}
          </span>
        ) : null}
        {creatingContact && selectedClientId ? (
          <ContactForm
            saveContact={(draft) => createContact(selectedClientId, draft)}
            onSaved={handleContactCreated}
            onCancel={() => setCreatingContact(false)}
          />
        ) : null}
        {contactOpen && !creatingContact && selectedClientId ? (
          <ul className={styles.list} id="contact-autocomplete-list" role="listbox">
            {contactRows.map((row, index) => {
              const isCommand = row.kind !== "contact";
              const primary = row.kind === "contact" ? row.contact.displayName : "Create Contact";
              const meta =
                row.kind === "contact"
                  ? [row.contact.email, row.contact.phone].filter(Boolean).join(" · ") || undefined
                  : "Add a contact to this client";
              return (
                <li
                  key={row.kind === "contact" ? row.contact.id : row.kind}
                  role="option"
                  aria-selected={index === contactActive}
                  className={`${styles.item}${isCommand ? ` ${styles.command}` : ""}${index === contactActive ? ` ${styles.itemActive}` : ""}`}
                  onMouseEnter={() => setContactActive(index)}
                  onMouseDown={(event) => {
                    event.preventDefault();
                    if (row.kind === "contact") chooseContact(row.contact);
                    else if (selectedClientId) setCreatingContact(true);
                  }}
                >
                  <span className={styles.primary}>{primary}</span>
                  {meta ? <span className={styles.meta}>{meta}</span> : null}
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
      )}
    </div>
  );
}
